import { wrapCreateBrowserRouterV7 } from '@sentry/react';
import {
  createBrowserRouter,
  createRoutesFromElements,
  Navigate,
  Outlet,
  Route,
  RouterProvider,
} from 'react-router-dom';
import { Hubspot } from './components/hubspot/Hubspot';
import { Toaster } from '@startuptools/ui/toast/toaster';
import { isNil } from 'lodash-es';
import { OldBrowser } from './OldBrowser';
import { OnboardingRoutes } from './onboarding/Onboarding';
import { CompanyRoutes } from './company/Company';
import { FreeTemplates } from './free-templates/FreeTemplates';
import { DocKind } from './free-templates/types';
import { GqlCompany } from './graphql/base-types.graphql';
import { useNavigate } from './components/Navigate';
import { useJwt } from './components/react-wrapper/UserIdContext';
import { Box } from '@startuptools/ui/Box';
import { GenericError } from '@startuptools/ui/Error';
import { AccessAdminCompany } from './access-admin-company/AccessAdminCompany';
import { NotFound } from './components/404';
import { Settings as LuxonSettings } from 'luxon';

const browserTooOld = isNil(File) || isNil(globalThis) || isNil([].toReversed);

const sentryCreateBrowserRouter = wrapCreateBrowserRouterV7(createBrowserRouter);

LuxonSettings.defaultLocale = window.location.pathname.startsWith('/sv') ? 'sv' : 'en';

export const AppRoutes = ({
  showNewDocumentDialog,
}: {
  showNewDocumentDialog: (
    kind: DocKind,
    company: Pick<GqlCompany, 'id' | 'name' | 'organizationNumber' | 'addressAsStr'>,
  ) => Promise<boolean>;
}) => {
  const router = sentryCreateBrowserRouter(
    createRoutesFromElements(
      <Route path="/:lang?" element={<App />} errorElement={<GenericError />}>
        <Route path="onboarding" element={<OnboardingRoutes />}>
          <Route path="*" element={<OnboardingRoutes />} />
        </Route>
        <Route path="company" element={<CompanyRoutes showNewDocumentDialog={showNewDocumentDialog} />}>
          <Route path="*" element={<CompanyRoutes showNewDocumentDialog={showNewDocumentDialog} />} />
        </Route>
        <Route path="free-templates" element={<FreeTemplates isInPlatform={false} />} />
        <Route path="admin-company/:companyId" element={<AccessAdminCompany />} />
        <Route path="*" element={<NotFound />} />
      </Route>,
    ),
  );

  return <RouterProvider router={router} />;
};

const App = () => {
  if (browserTooOld) {
    return <OldBrowser />;
  }
  const { isValid } = useJwt();

  const { pathname, redirectoToAuth, authPath, rootPath, companyRootPath } = useNavigate();

  if (!isValid && !pathname.startsWith(authPath)) {
    redirectoToAuth();
    return '';
  } else if (pathname === rootPath) {
    return <Navigate to={companyRootPath} replace />;
  }

  return (
    <Box sx={{ height: '100dvh', overflow: 'hidden' }}>
      <Outlet />
      <Hubspot />
      <Toaster />
    </Box>
  );
};
